import { CloudDownload } from 'lucide-react';
import {
  ImportItemsStep,
  type ImportSource,
} from 'src/components/ImportProposalsModal/import-items-step';
import type { ImportListRow } from 'src/components/ImportProposalsModal/use-import-list-rows';
import {
  ModalContainer,
  ModalList,
  type ModalListItem,
  ModalStep,
  useModalSteps,
} from 'src/components/ModalContainer';
import { useGetDtoolsAuthDetails } from 'src/graphql/entities/integrations/dtools/hooks/use-get-dtools-auth-details';
import { useGetPortalioAuthDetails } from 'src/graphql/entities/integrations/portalio/hooks/use-get-portalio-auth-details';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';

const NOT_CONNECTED = 'Not connected — set it up in Settings → Integrations';

interface ImportSourceStepProps {
  onImport: (items: ImportListRow[], source: ImportSource) => void;
  onClose: () => void;
}

const ImportSourceStep = ({ onImport, onClose }: ImportSourceStepProps) => {
  const { pushStep } = useModalSteps();
  const { portalioAuthDetails } = useGetPortalioAuthDetails();
  const { isConnected: isDtoolsConnected } = useGetDtoolsAuthDetails();
  const isPortalioConnected = Boolean(portalioAuthDetails?.id);

  const sources: (ModalListItem & { id: ImportSource })[] = [
    {
      id: 'portalio',
      title: 'portal.io',
      description: isPortalioConnected ? 'Import proposals from portal.io' : NOT_CONNECTED,
      icon: <CloudDownload />,
      disabled: !isPortalioConnected,
    },
    {
      id: 'dtools',
      title: 'D-Tools Cloud',
      description: isDtoolsConnected ? 'Import proposals from D-Tools Cloud' : NOT_CONNECTED,
      icon: <CloudDownload />,
      disabled: !isDtoolsConnected,
    },
  ];

  return (
    <ModalStep title="Import from…">
      <ModalList
        items={sources}
        onSelect={({ id }) =>
          void pushStep(
            <ImportItemsStep
              source={id}
              onImport={(items) => onImport(items, id)}
              onCancel={onClose}
            />,
          )
        }
        placeholder="Choose a source"
        testId="import-proposals-source"
      />
    </ModalStep>
  );
};

/** Import proposals as site files: choose portal.io or D-Tools Cloud, then the items. */
export const ImportProposalsModal = () => {
  const { closeModal } = useModalMethods(Modals.ImportProposals);
  const { isOpened, modalProps } = useModalState(Modals.ImportProposals);

  const handleImport = (items: ImportListRow[], source: ImportSource) => {
    modalProps?.onItemsSelected(
      items.map(({ id, name }) => ({ id, name })),
      source,
    );
    closeModal();
  };

  return (
    <ModalContainer
      open={isOpened}
      size="large"
      onClose={closeModal}
      data-testid="import-proposals-modal"
    >
      <ImportSourceStep onImport={handleImport} onClose={closeModal} />
    </ModalContainer>
  );
};
